import { Injectable, Injector } from '@angular/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { Haptics, NotificationType as HapticsNotificationType } from '@capacitor/haptics';
import { HttpIoService } from '../communication/http-io.service';
import { IClient, IRegisterDeviceDTO, IUserDevice } from 'common_library';
import { distinctUntilChanged, map } from 'rxjs';
import { FCM } from "@capacitor-community/fcm";
import { MainStateService } from '../state/app-main-state.service';
import { ChargingSessionLive } from 'src/app/core/charging-live/charging-session-live.plugin';
import { AppNativeService } from '../app.native.service';
import { TranslateConfigService } from '../translate.service';
import { DeepLinkService } from './deep-link.service';
import { AuthService } from '../auth/auth.service';

const CONTROLLER_ROOT = 'client';
const DEVICE_CONTROLLER_ROOT = 'device';

@Injectable({ providedIn: 'root' })
export class PushNotificationsNativeService {

  private token: string;
  userId: string;
  readonly clientId: string;

  constructor(
    private HIO: HttpIoService,
    private auth: AuthService,
    private mainStateService: MainStateService,
    private translateConfigService: TranslateConfigService,
    private deepLink: DeepLinkService,
    // Injector per risolvere AppNativeService in modo lazy (AppNativeService dipende già da questo service)
    private injector: Injector
  ) {
    this.clientId = this.mainStateService.getClientId();
    const userId$ = this.mainStateService.user$.pipe(map(u => u?.id), distinctUntilChanged())

    console.log('[PUSH] PushNotificationsNativeService: Constructor called. Client ID:', JSON.stringify(this.clientId));

    userId$.subscribe((userId) => {
      console.log('[PUSH] User ID subscription: User ID changed to:', JSON.stringify(userId));

      if (!this.isPushAvailable()) {
        console.warn('[PUSH] Push Notifications are not available on this platform. User ID subscription check.');
        return;
      }
      if (!!userId) {
        console.log('[PUSH] User detected, initializing push notifications for user:', JSON.stringify(userId));
        this.init();
      } else {
        console.log('[PUSH] User logged out or not detected, cleaning up push notifications.');
        this.clean();
      }
    });
  }

  getToken() {
    console.log('[PUSH] getToken: Returning current token:', JSON.stringify({
      tokenPreview: this.tokenPreview(this.token),
      tokenLength: this.token?.length || 0,
    }));
    return this.token;
  }

  setToken(token: string) {
    this.token = token;
    console.log('[PUSH] setToken: Token locally set:', JSON.stringify({
      tokenPreview: this.tokenPreview(token),
      tokenLength: token?.length || 0,
    }));
  }

  async init() {
    console.log('[PUSH] init: Starting push notification initialization process.');

    if (!this.isPushAvailable()) {
      console.warn('[PUSH] Push Notifications are not available on this platform. INIT aborted.');
      return;
    }

    await PushNotifications.addListener('registration', async (token: Token) => {
      this.setToken(token.value);
      console.log('[PUSH] registration event: Push registration success:', JSON.stringify({
        tokenPreview: this.tokenPreview(token.value),
        tokenLength: token.value?.length || 0,
      }));
      await this.updateUserRegisterationToken(token.value);
    });

    await PushNotifications.addListener('registrationError', (error: any) => {
      console.error('[PUSH] registrationError event: Error on registration:', JSON.stringify(error));
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      console.log('[PUSH] pushNotificationReceived event: Push notification received:', JSON.stringify(notification));
      this.vibrateForNotification();
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log('[PUSH] pushNotificationActionPerformed event: Push action performed:', JSON.stringify(notification));
      // Tap su una notifica: il DeepLinkService legge il data payload (ct,
      // open_app_info, installation_id, open, uid/notification_uid), registra
      // l'eventuale click campagna e instrada l'apertura (router in app.component).
      this.deepLink.handlePushData(notification?.notification?.data);
    });

    await this.requestPermissions();
  }

  async requestPermissions() {
    console.log('[PUSH] requestPermissions: Attempting to request push notification permissions.');

    if (!this.isPushAvailable()) {
      console.warn('[PUSH] Push Notifications are not available on this platform. Permissions request aborted.');
      return;
    }

    if (Capacitor.getPlatform() !== 'web') {
      const permissionStatus = await PushNotifications.requestPermissions();
      console.log('[PUSH] requestPermissions: Push Notifications permission status:', JSON.stringify(permissionStatus));

      if (permissionStatus.receive === 'granted') {
        console.log('[PUSH] requestPermissions: Push Notifications permission granted. Registering for push.');
        await PushNotifications.register();
      } else {
        console.warn('[PUSH] requestPermissions: Push Notifications permission denied or not granted.');
        //TODO: Some function with the user who does not give permission
      }
    } else {
      console.log('[PUSH] requestPermissions: Running on web platform, skipping native permission request.');
    }
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const fcmToken = await FCM.getToken();
        console.log('[PUSH] FCM.getToken() iOS OK:', JSON.stringify({
          tokenPreview: this.tokenPreview(fcmToken.token),
          tokenLength: fcmToken.token?.length || 0,
        }));
        this.setToken(fcmToken.token);
        await this.updateUserRegisterationToken(fcmToken.token);
      } catch (err) {
        console.error('[PUSH] Error getting FCM token on iOS:', err);
      }
    }
    if (Capacitor.getPlatform() === 'android') {
      try {
        const fcmToken = await FCM.getToken();
        console.log('[PUSH] FCM.getToken() Android OK:', JSON.stringify({
          tokenPreview: this.tokenPreview(fcmToken.token),
          tokenLength: fcmToken.token?.length || 0,
        }));
        this.setToken(fcmToken.token);
        await this.updateUserRegisterationToken(fcmToken.token);
      } catch (err) {
        console.error('[PUSH] Error getting FCM token on iOS:', err);
      }
    }
  };

  async resetPushNotifications() {
    console.log('[PUSH] resetPushNotifications: Removing all delivered notifications.');
    await PushNotifications.removeAllDeliveredNotifications();
  };

  public async clean() {
    console.log('[PUSH] clean: Cleaning up push notifications and FCM instance.');
    if (this.clientId) {
      await PushNotifications.removeAllDeliveredNotifications();
      // PRIMA revoco il device sul backend (serve il token memorizzato), POI elimino l'istanza FCM
      await this.revokeDeviceRegistration();
      try {
        await FCM.deleteInstance();
        console.log('[PUSH] clean: FCM instance deleted successfully.');
      } catch (fcmError) {
        console.error('[PUSH] clean: Error deleting FCM instance:', JSON.stringify(fcmError));
      }
    } else {
      console.warn('[PUSH] clean: No client ID available for cleanup.');
    }
  }

  private async revokeDeviceRegistration(): Promise<void> {
    if (!this.token) {
      console.warn('[PUSH] revokeDeviceRegistration: No token available, skipping device revoke.');
      return;
    }
    try {
      await this.HIO.deleteItem(DEVICE_CONTROLLER_ROOT, encodeURIComponent(this.token));
      console.log('[PUSH] revokeDeviceRegistration: Device revoked on backend:', JSON.stringify({
        tokenPreview: this.tokenPreview(this.token),
      }));
    } catch (error) {
      console.error('[PUSH] revokeDeviceRegistration: Error revoking device on backend:', JSON.stringify(error));
    }
  }

  private isPushAvailable() {
    const available = Capacitor.isPluginAvailable('PushNotifications');
    console.log(`[PUSH] isPushAvailable: PushNotifications plugin available: ${available}`);
    return available;
  }

  private isFCMAvailable() {
    const available = Capacitor.isPluginAvailable('FCM');
    console.log(`[PUSH] isFCMAvailable: FCM plugin available: ${available}`);
    return available;
  }

  private async vibrateForNotification(): Promise<void> {
    try {
      await Haptics.notification({ type: HapticsNotificationType.Success });
      if (Capacitor.getPlatform() === 'android') {
        await Haptics.vibrate({ duration: 250 });
      }
    } catch (error) {
      console.warn('[PUSH] Unable to vibrate for push notification:', error);
    }
  }

  async updateUserRegisterationToken(token: string): Promise<void> {
    // Durante l'impersonation NON si registra/sposta il token: il backend ha già
    // prestato i device dell'admin all'utente simulato (revert allo stop). Registrare
    // qui sovrascriverebbe quel prestito con un clientId dedicato → niente revert pulito.
    if (this.auth.isImpersonating) {
      console.log('[PUSH] updateUserRegisterationToken: skipped (impersonation active).');
      return;
    }
    console.log('[PUSH] updateUserRegisterationToken: Attempting to update user registration token on backend.');
    try {
      const platformType = this.mainStateService.getPlatformType();
      const supportsLiveActivity = await this.resolveSupportsLiveActivity();
      const dto: Partial<IClient> = {
        wpEndpoint: {
          fcmToken: token,
          platformType,
          supportsLiveActivity,
        }
      }
      console.log("[PUSH] updateUserRegisterationToken: DTO being sent to backend for client registration:", JSON.stringify({
        clientId: this.clientId,
        platformType,
        supportsLiveActivity,
        tokenPreview: this.tokenPreview(token),
        tokenLength: token?.length || 0,
      }));
      const rv = await this.HIO.patchItem<IClient, Partial<IClient>>('client', this.clientId, dto)
      console.log('[PUSH] updateUserRegisterationToken: Push registration token updated successfully on backend. Response:', JSON.stringify({
        clientId: rv?.id,
        platformType: (rv?.wpEndpoint as any)?.platformType,
        supportsLiveActivity: (rv?.wpEndpoint as any)?.supportsLiveActivity,
        hasFcmToken: !!(rv?.wpEndpoint as any)?.fcmToken,
      }));
    } catch (error) {
      console.error('[PUSH] updateUserRegisterationToken: Error updating registration token on backend:', JSON.stringify(error));
    }
    // Nuova registrazione device (notification-service): errori gestiti internamente, non bloccano il flusso
    await this.registerDevice(token);
  }

  /** Registra il device sul nuovo endpoint `device` (user_device). L'enforcement delle preferenze è server-side (niente topic FCM). */
  private async registerDevice(token: string): Promise<void> {
    try {
      const dto: IRegisterDeviceDTO = {
        fcmToken: token,
        platform: Capacitor.getPlatform(), // 'ios' | 'android'
        appVersion: await this.resolveAppVersion(),
        locale: this.translateConfigService.getCurrentLang() || undefined,
      };
      console.log('[PUSH] registerDevice: DTO being sent to backend for device registration:', JSON.stringify({
        platform: dto.platform,
        appVersion: dto.appVersion,
        locale: dto.locale,
        tokenPreview: this.tokenPreview(token),
      }));
      const rv = await this.HIO.postItem<IUserDevice, IRegisterDeviceDTO>(DEVICE_CONTROLLER_ROOT, dto);
      console.log('[PUSH] registerDevice: Device registered successfully on backend:', JSON.stringify({
        deviceId: rv?.id,
        platform: rv?.platform,
      }));
    } catch (error) {
      console.error('[PUSH] registerDevice: Error registering device on backend:', JSON.stringify(error));
    }
  }

  private async resolveAppVersion(): Promise<string | undefined> {
    try {
      // Lazy injection: AppNativeService dipende a sua volta da questo service
      const appNativeService = this.injector.get(AppNativeService);
      return await appNativeService.getCurrentAppVersionName();
    } catch (error) {
      console.warn('[PUSH] resolveAppVersion: Unable to resolve app version:', error);
      return undefined;
    }
  }

  private async resolveSupportsLiveActivity(): Promise<boolean> {
    const platform = Capacitor.getPlatform();
    const native = Capacitor.isNativePlatform();
    const pluginAvailable = Capacitor.isPluginAvailable('ChargingSessionLive');
    console.log('[PUSH] resolveSupportsLiveActivity: start', JSON.stringify({
      platform,
      native,
      pluginAvailable,
    }));

    if (!native) {
      console.log('[PUSH] resolveSupportsLiveActivity: false, not native');
      return false;
    }
    if (!pluginAvailable) {
      console.warn('[PUSH] resolveSupportsLiveActivity: false, ChargingSessionLive plugin unavailable');
      return false;
    }

    if (platform === 'android') {
      console.log('[PUSH] resolveSupportsLiveActivity: true, Android foreground notification plugin available');
      return true;
    }
    if (platform !== 'ios') {
      console.log('[PUSH] resolveSupportsLiveActivity: false, unsupported platform');
      return false;
    }

    try {
      const result = await ChargingSessionLive.requestPermissions();
      const granted = result?.granted === true;
      console.log('[PUSH] resolveSupportsLiveActivity: iOS ActivityKit permission/capability', JSON.stringify({
        granted,
        raw: result,
        mode: 'ActivityKit decides Dynamic Island vs Lock Screen Live Activity automatically',
      }));
      return granted;
    } catch (error) {
      console.warn('[PUSH] Live Activity capability check failed:', error);
      return false;
    }
  }

  private tokenPreview(token?: string): string | null {
    if (!token) return null;
    if (token.length <= 12) return token;
    return `${token.substring(0, 6)}...${token.substring(token.length - 6)}`;
  }
}
